import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title, Meta } from '@angular/platform-browser';
import { ReCaptcha2Component } from 'ngx-captcha';
import { finalize } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/services/environment.service';
import { FacilityService } from 'src/app/services/facility.service';
import {
  CorporateWellnessContent,
  CorporateWellnessPageConfig,
  FacilityConfig,
  FormInterestOption,
} from 'src/environments/environment.interface';
import { mergeCorporateContent, setCanonicalHref } from '../steelhaus-page.util';

/**
 * Steelhaus "Corporate Wellness" partnership landing page.
 * Inquiry posts to Facility/CorporateWellnessRequest (separate from Contact Us).
 * Captcha is a client-side UI gate only — same as Contact Us / SupportRequest
 * (token is not sent or verified server-side; API uses IP rate limiting).
 */
@Component({
  selector: 'app-corporate-wellness',
  templateUrl: './corporate-wellness.component.html',
  styleUrls: ['./corporate-wellness.component.scss'],
})
export class CorporateWellnessComponent implements OnInit {
  @ViewChild(ReCaptcha2Component) captchaComponent?: ReCaptcha2Component;
  settings: FacilityConfig;
  pageConfig?: CorporateWellnessPageConfig;
  showPage = false;
  submitting = false;
  success = false;
  error = '';
  form: FormGroup;
  siteKey = '';
  captchaIsLoaded = false;
  captchaSuccess = false;
  captchaIsExpired = false;
  captchaResponse?: string;

  content: Required<CorporateWellnessContent>;

  constructor(
    private env: EnvironmentService,
    private title: Title,
    private meta: Meta,
    private fb: FormBuilder,
    private facilityService: FacilityService
  ) {
    this.settings = this.env.getFacilityConfig();
    this.pageConfig = this.settings.corporateWellnessPage;
    this.content = mergeCorporateContent(this.pageConfig?.content);
    this.siteKey = this.settings.recaptcha?.siteKey ?? '';
    this.form = this.fb.group({
      company: ['', Validators.required],
      name: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$'),
        ],
      ],
      phone: [''],
      size: [''],
      interests: this.fb.group(this.buildInterestControls()),
      message: [''],
      captchaResponseControl: ['', Validators.required],
    });
  }

  get interestOptions(): FormInterestOption[] {
    return this.content.contact?.interestOptions ?? [];
  }

  private buildInterestControls(): Record<string, [boolean]> {
    return Object.fromEntries(this.interestOptions.map((o) => [o.key, [false]]));
  }

  ngOnInit(): void {
    this.showPage = !!this.pageConfig?.show;
    if (!this.showPage) {
      return;
    }
    const pageTitle = `${this.pageConfig?.metaTitle ?? 'Corporate Wellness'} — ${this.settings.name}`;
    const desc =
      this.pageConfig?.metaDescription ??
      "Bring your team's wellbeing into focus — private group rides, subsidized memberships, and flexible ride packs for your company.";
    this.title.setTitle(pageTitle);
    this.setSocialMeta(pageTitle, desc);
  }

  private setSocialMeta(pageTitle: string, description: string): void {
    const meta = (this.settings.meta as any) || {};
    const url = typeof window !== 'undefined' ? window.location.href : '';
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: meta.ogImage || '' });
    this.meta.updateTag({ property: 'og:url', content: url });
    setCanonicalHref(url);
  }

  handleSuccess(captchaResponse: string | Event): void {
    const token =
      typeof captchaResponse === 'string'
        ? captchaResponse
        : (captchaResponse.target as HTMLInputElement | undefined)?.value ?? '';
    this.captchaResponse = token;
    this.form.controls['captchaResponseControl'].setValue(token);
    this.captchaSuccess = true;
    this.captchaIsExpired = false;
  }

  handleLoad(): void {
    this.captchaIsLoaded = true;
  }

  handleExpire(): void {
    this.captchaIsExpired = true;
    this.captchaSuccess = false;
    this.form.controls['captchaResponseControl'].reset();
  }

  resetCaptcha(): void {
    this.captchaSuccess = false;
    this.captchaIsExpired = false;
    this.captchaResponse = undefined;
    this.form.controls['captchaResponseControl'].reset();
    this.captchaComponent?.resetCaptcha();
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  private canSubmit(): boolean {
    const { company, name, email, captchaResponseControl } = this.form.getRawValue();
    if (!company?.trim() || !name?.trim() || !email?.trim()) {
      return false;
    }
    if (this.form.controls['email'].invalid) {
      return false;
    }
    return !!captchaResponseControl?.trim() && this.form.controls['captchaResponseControl'].valid;
  }

  onSubmit(): void {
    if (this.submitting) {
      return;
    }

    this.success = false;
    this.error = '';

    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      this.error = 'Please complete all required fields and captcha.';
      return;
    }

    this.submitting = true;

    const v = this.form.getRawValue();
    const interests = this.interestOptions
      .filter((o) => (v.interests as Record<string, boolean>)[o.key])
      .map((o) => o.label)
      .join(', ');

    this.facilityService
      .submitCorporateWellnessRequest({
        company: v.company,
        name: v.name,
        email: v.email,
        phone: v.phone || '',
        companySize: v.size || '',
        interests: interests || '',
        message: v.message || '',
      })
      .pipe(finalize(() => {
        this.submitting = false;
      }))
      .subscribe({
        next: () => {
          this.success = true;
          this.form.reset({
            company: '',
            name: '',
            email: '',
            phone: '',
            size: '',
            interests: this.buildInterestControlsReset(),
            message: '',
            captchaResponseControl: '',
          });
          this.resetCaptcha();
        },
        error: (err) => {
          console.error('Error submitting corporate wellness form:', err);
          this.error =
            err?.error?.message ||
            `Sorry — something went wrong. Please try again, or email us at ${this.content.contact.email}.`;
          this.resetCaptcha();
        },
      });
  }

  private buildInterestControlsReset(): Record<string, boolean> {
    return Object.fromEntries(this.interestOptions.map((o) => [o.key, false]));
  }
}
